import { useState, useEffect } from "react";
import { useGPXParser } from "./use-gpx-parser";
import { LngLatBounds } from "mapbox-gl";
import mapboxgl from "mapbox-gl";

export interface Coordinate {
  lat: number;
  lng: number;
  elevation?: number;
  time?: number;
}

export interface CoordinateBounds {
  northEast: Coordinate;
  southWest: Coordinate;
}

export const mapboxLngLatBoundsFromCoordinateBounds = (
  bounds: CoordinateBounds
): mapboxgl.LngLatBounds => {
  const ne = bounds?.northEast;
  const sw = bounds?.southWest;

  if (!ne || !sw) {
    console.warn("WARN: Missing northeast or southwest!", { ne, sw });
  }

  const neLat = parseFloat(String(ne.lat));
  const neLng = parseFloat(String(ne.lng));
  const swLat = parseFloat(String(sw.lat));
  const swLng = parseFloat(String(sw.lng));

  if (isNaN(neLat) || isNaN(neLng) || isNaN(swLat) || isNaN(swLng)) {
    console.error("ERROR: One or more coordinate values are NaN!", {
      neLat,
      neLng,
      swLat,
      swLng,
      original: { ne, sw },
    });
    throw new Error("NaN in bounds conversion");
  }

  return new mapboxgl.LngLatBounds([swLng, swLat], [neLng, neLat]);
};

export const defaultLngLatBounds = new mapboxgl.LngLatBounds(
  [-122.4194, 37.7749], // San Francisco
  [-122.4194, 37.7749] // San Francisco
);

export const useCoordinates = (step: number = 10) => {
  const [coordinates, setCoordinates] = useState<Coordinate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCoordinates = async () => {
    try {
      const response = await fetch("/TestCoordinatesWHW.json");
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data: Coordinate[] = await response.json();
      const filteredData = data.filter((_, index) => index % step === 0);

      setCoordinates(filteredData);

      setLoading(false);
    } catch (err) {
      setError((err as Error).message);
      setLoading(false);
    }
  };

  fetchCoordinates();

  return { coordinates, loading, error };
};

export const useCoordinatesFromGPX = (step: number = 1) => {
  const [coordinates, setCoordinates] = useState<Coordinate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { parse } = useGPXParser({});

  // useEffect(() => {
  //   const fetchCoordinates = async () => {
  //     try {
  //       const response = await fetch("/Morning_Run.gpx");
  //       if (!response.ok) {
  //         throw new Error("Network response was not ok");
  //       }
  //       const data: string = await response.text();
  //       const coordinates = getCoordinatesFromGPXString(data, 10000);
  //       setCoordinates(coordinates);
  //     } catch (err) {
  //       setError((err as Error).message);
  //       setLoading(false);
  //     }
  //   };
  //   fetchCoordinates();
  // }, []);

  const getCoordinatesFromGPXString = (
    gpxString: string,
    desiredLength?: number
  ): Coordinate[] => {
    const geoJSON = parse(gpxString);
    const coordinates = geoJSON.features[0].geometry.coordinates;
    // Each coordinate is an array of [lng, lat, eleveation]
    const array = coordinates
      .map((coord) => {
        const array = coord as number[];
        if (array.length > 1) {
          return { lat: array[1], lng: array[0] };
        }
        return null;
      })
      .filter((coord): coord is Coordinate => coord !== null);
    if (desiredLength) {
      const paredDown = reduceCoordinatesFavouringDistance(
        array,
        desiredLength
      );
      return paredDown;
    }
    return array;
  };

  return {
    coordinates,
    reduceCoordinates,
    reduceCoordinatesFavouringDistance,
    loading,
    error,
  };
};

export const reduceCoordinates = (
  coordinates: Coordinate[],
  desiredLength: number
): Coordinate[] => {
  const currentLength = coordinates.length;

  if (desiredLength >= currentLength || desiredLength <= 0) {
    // Return the original array if desiredLength is greater than or equal to current length
    // or if the desired length is invalid (e.g., 0 or negative)
    return coordinates;
  }

  // Calculate the interval for removing elements
  const step = Math.floor(currentLength / desiredLength);
  const result = coordinates.filter((_, index) => index % step === 0);

  return result;
};

export function reduceCoordinatesFavouringDistance(
  coordinates: Coordinate[],
  targetCount: number
): Coordinate[] {
  if (targetCount >= coordinates.length) {
    return coordinates;
  }

  const result = [...coordinates];

  while (result.length > targetCount) {
    let minDistance = Infinity;
    let indexToRemove = -1;

    for (let i = 0; i < result.length - 1; i++) {
      const d = distance(result[i], result[i + 1]);
      if (d < minDistance) {
        minDistance = d;
        indexToRemove = i + 1;
      }
    }

    result.splice(indexToRemove, 1);
  }

  return result;
}

export function distance(coord1: Coordinate, coord2: Coordinate): number {
  return Math.sqrt(
    Math.pow(coord1.lat - coord2.lat, 2) + Math.pow(coord1.lng - coord2.lng, 2)
  );
}
